import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { PlayCircle, X, ArrowRight } from 'lucide-react';
import { useI18n } from '@/i18n/LanguageContext';
import { useOverlayLock } from '@/lib/overlay';
import TutorialPlayer from '@/components/tutorial/TutorialPlayer';
import WorkflowDiagram from '@/components/tutorial/WorkflowDiagram';
import { TUTORIAL_PATH, TutorialAudience } from '@/data/tutorial';

interface Props {
  audience?: TutorialAudience;
  placement?: string;
  variant?: 'solid' | 'outline' | 'ghost' | 'light';
  className?: string;
  label?: string;
}

/** Opens the "How GIGGONE Works" tutorial in a modal. Esc / backdrop closes. */
const WatchDemoButton: React.FC<Props> = ({ audience = 'sub', placement = 'button', variant = 'outline', className = '', label }) => {
  const { lang, path } = useI18n();
  const [open, setOpen] = useState(false);
  useOverlayLock(open);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false);
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open]);

  const styles = {
    solid: 'bg-orange-500 text-white hover:bg-orange-600',
    outline: 'border-2 border-slate-300 bg-white text-slate-800 hover:border-slate-400 hover:bg-slate-50',
    ghost: 'text-slate-700 hover:bg-slate-100',
    light: 'border border-white/25 bg-white/10 text-white hover:bg-white/20',
  }[variant];

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        className={`inline-flex min-h-[44px] items-center justify-center gap-2 rounded-xl px-4 py-2.5 text-sm font-bold transition-colors ${styles} ${className}`}
      >
        <PlayCircle className="h-5 w-5" />
        {label || (lang === 'es' ? 'Ver demo' : 'Watch Demo')}
      </button>

      {open && (
        <div
          className="fixed inset-0 z-[130] flex items-start justify-center overflow-y-auto bg-slate-950/80 p-3 sm:items-center sm:p-6"
          onClick={() => setOpen(false)}
          role="dialog"
          aria-modal="true"
          aria-label={lang === 'es' ? 'Cómo funciona GIGGONE' : 'How GIGGONE Works'}
        >
          <div className="relative w-full max-w-5xl" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute -top-2 right-0 z-10 -translate-y-full rounded-full bg-white/10 p-2 text-white hover:bg-white/20"
              aria-label={lang === 'es' ? 'Cerrar' : 'Close'}
            >
              <X className="h-5 w-5" />
            </button>
            <TutorialPlayer defaultAudience={audience} autoPlay placement={placement} className="mt-10 sm:mt-8" />
            <div className="mt-4 rounded-2xl bg-white p-4 sm:p-5">
              <WorkflowDiagram compact />
              <div className="mt-4 flex justify-end">
                <Link
                  to={path(TUTORIAL_PATH)}
                  onClick={() => setOpen(false)}
                  className="inline-flex items-center gap-1.5 text-sm font-bold text-orange-600 hover:text-orange-700"
                >
                  {lang === 'es' ? 'Guía completa paso a paso' : 'Full step-by-step guide'} <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default WatchDemoButton;
